import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdViewColumn } from '../../onam/icons'; // ONAM glyphs (was react-icons/md)
import styles from './columnVisibilityToggle.module.css';
import { Tooltip } from '../Tooltip';

export interface ColumnDef {
  key: string;
  label: string;
  alwaysVisible?: boolean;
}

interface Props {
  columns: ColumnDef[];
  hidden: Set<string>;
  onChange: (key: string, visible: boolean) => void;
}

const OFF_SCREEN: React.CSSProperties = { position: 'fixed', top: -9999, left: -9999 };

const ColumnVisibilityToggle: React.FC<Props> = ({ columns, hidden, onChange }) => {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>(OFF_SCREEN);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Measure actual panel width after render, then snap to correct position
  useLayoutEffect(() => {
    if (!open || !btnRef.current || !panelRef.current) return;
    const btnRect = btnRef.current.getBoundingClientRect();
    const panelWidth = panelRef.current.offsetWidth;
    setPanelStyle({
      position: 'fixed',
      top: btnRect.bottom + 6,
      left: Math.max(8, btnRect.right - panelWidth),
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPanelStyle(OFF_SCREEN);
      return;
    }
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      <Tooltip text="Show / hide columns" placement="bottom">
        <button
          ref={btnRef}
          type="button"
          className={styles.toggleBtn}
          onClick={() => setOpen(prev => !prev)}
          aria-label="Toggle column visibility"
          aria-expanded={open}
        >
          <MdViewColumn size={18} />
        </button>
      </Tooltip>

      {open && createPortal(
        <div ref={panelRef} className={styles.panel} style={panelStyle} role="menu">
          <div className={styles.panelTitle}>Columns</div>
          {columns.map(col => (
            <label key={col.key} className={styles.row}>
              <input
                type="checkbox"
                checked={!hidden.has(col.key)}
                disabled={col.alwaysVisible}
                onChange={e => !col.alwaysVisible && onChange(col.key, e.target.checked)}
              />
              <span>{col.label}</span>
            </label>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColumnVisibilityToggle;
